import { useEffect, useRef } from "react";

type BreadcrumbItem = { label: string; href?: string };

type Props = {
  locale: string;
  breadcrumbs: BreadcrumbItem[];
  lastReviewedAt: string;
  reviewedBy: string;
  children: React.ReactNode;
};

const REVIEWED_LABEL: Record<string, string> = {
  en: "Reviewed by",
  ar: "مراجعة بواسطة",
  fr: "Révisé par",
};
const LAST_REVIEWED: Record<string, string> = {
  en: "Last reviewed",
  ar: "آخر مراجعة",
  fr: "Dernière révision",
};

export function DocLayout({ locale, breadcrumbs, lastReviewedAt, reviewedBy, children }: Props) {
  const articleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (articleRef.current) {
      const tables = articleRef.current.querySelectorAll("table");
      for (const table of tables) {
        if (!table.parentElement?.classList.contains("table-wrapper")) {
          const wrapper = document.createElement("div");
          wrapper.className = "table-wrapper overflow-x-auto";
          table.parentNode?.insertBefore(wrapper, table);
          wrapper.appendChild(table);
        }
      }
    }
  }, []);

  return (
    <article ref={articleRef} className="doc-page max-w-3xl mx-auto px-4 py-8 sm:py-12">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          {breadcrumbs.map((crumb, i) => (
            <li key={`${crumb.label}-${i}`} className="flex items-center gap-1">
              {i > 0 && (
                <span className="mx-1" aria-hidden="true">
                  /
                </span>
              )}
              {crumb.href ? (
                <a
                  href={crumb.href}
                  className="hover:text-foreground transition-colors duration-150 underline-offset-2 hover:underline"
                >
                  {crumb.label}
                </a>
              ) : (
                <span aria-current="page">{crumb.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <div className="doc-content prose prose-neutral dark:prose-invert max-w-none">{children}</div>
      <footer className="mt-12 pt-6 border-t border-border text-sm text-muted-foreground">
        <p>
          {REVIEWED_LABEL[locale] ?? REVIEWED_LABEL.en}: {reviewedBy}
        </p>
        <p>
          {LAST_REVIEWED[locale] ?? LAST_REVIEWED.en}: {lastReviewedAt}
        </p>
      </footer>
    </article>
  );
}
